// src/pages/CreateEvent.js
import React, { useRef, useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import EventForm from '../components/EventForm';
import Button from '../components/Button/Button';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import Typography from '../components/Typography';

const CreateEvent = () => {
  const { t } = useTranslation('createEvent');
  const { user, refreshUserData, stripeStatus, refreshStripeStatus } = useAuth();
  const navigate = useNavigate();
  const eventFormRef = useRef();
  const [loadingCreate, setLoadingCreate] = useState(false);

  // Refrescar datos del usuario cuando se monta el componente
  // para asegurar que tenemos la información más actualizada de Stripe
  useEffect(() => {
    if (user?.id) {
      refreshUserData();
      refreshStripeStatus(user.id);
    }
  }, [user?.id, refreshUserData, refreshStripeStatus]);

  const handleDiscard = () => {
    navigate('/discover');
  };

  const handleCreateEvent = async () => {
    if (loadingCreate) return;
    if (eventFormRef.current) {
      setLoadingCreate(true);
      try {
        const formData = await eventFormRef.current.getFormData();
        if (!formData) {
          console.error('Errores en el formulario, no se puede enviar');
          setLoadingCreate(false);
          return;
        }
        // Verificar que si hay tickets de pago, el usuario tenga activados los cobros
        const tickets = JSON.parse(formData.get('tickets') || '[]');
        const hasPaidTicket = tickets.some(ticket => ticket.type === 'paid');
        const userId = user?._id || user?.id;
        const currentStripeStatus = hasPaidTicket && userId
          ? await refreshStripeStatus(userId)
          : stripeStatus;

        if (hasPaidTicket && (!currentStripeStatus?.hasStripeAccount || !currentStripeStatus?.chargesEnabled)) {
          toast.error('No puedes crear eventos de pago sin tener una cuenta de Stripe validada y con cobros activados. Por favor, activa los pagos en tu configuración.');
          setLoadingCreate(false);
          return;
        }
        const response = await axiosClient.post('/api/events', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data.success) {
          toast.success(t('createEventPage.success'));
          navigate(`/events/manage/${response.data.event.id}`, {
            state: { showPublishModal: true, eventData: response.data.event }
          });
        } else {
          toast.error(t('createEventPage.error'));
          console.error('Error en la creación del evento:', response.data.message);
        }
      } catch (error) {
        toast.error(t('createEventPage.error'));
        console.error('Error creando el evento:', error.response?.data?.message || error.message);
      } finally {
        setLoadingCreate(false);
      }
    }
  };

  return (
    <>
      <Topbar>
        <Container>
          <Typography as="p" $variant="title-4-semibold" $variantTablet="title-4-semibold" $variantMobile="body-1-semibold">{t('createEventPage.title')}</Typography>
          <Links>
            <Button size="l" sizeTablet="l" sizeMobile="m" $variant="outline" onClick={handleDiscard}>
              {t('createEventPage.discard')}
            </Button>
            <Button size="l" sizeTablet="l" sizeMobile="m" $variant="default" onClick={handleCreateEvent} disabled={loadingCreate}>
              {loadingCreate ? t('createEventPage.creating') : t('createEventPage.create')}
            </Button>
          </Links>
        </Container>
      </Topbar>
      <EventForm ref={eventFormRef} onSubmit={handleCreateEvent} loadingCreate={loadingCreate} />
    </>
  );
};

export default CreateEvent;

const Topbar = styled.header`
  align-items: center;
  background: ${({ theme }) => theme.fill.defaultSubtle};
  border-bottom: 1px solid ${({ theme }) => theme.border.defaultWeak};
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1;
`;

const Container = styled.nav`
  align-items: center;
  display: flex;
  justify-content: space-between;
  max-width: 1400px;
  padding: ${({ theme }) => theme.sizing.sm} ${({ theme }) => theme.sizing.md};
  width: 100%;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 12px ${({ theme }) => theme.sizing.sm};
  }
`;

const Links = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
`;
